import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface CountdownTimerReturn {
    timeLeft: number;
    start: () => void;
    reset: () => void;
    isFinished: boolean;
    setNewDuration: (newDuration: number) => void;
    formattedTime: string;
}

export const useCountdownTimer = (initialDuration: number = 30): CountdownTimerReturn => {
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const [duration, setDuration] = useState(initialDuration);
    const [timeLeft, setTimeLeft] = useState(duration);
    const [isRunning, setIsRunning] = useState(false);
    const [isFinished, setIsFinished] = useState(false);

    useEffect(() => {
        return () => clearInterval(intervalRef.current as NodeJS.Timeout);
    }, []);

    const intervalCallback = useCallback(() => {
        setTimeLeft((prevTimeLeft) => {
            if (prevTimeLeft > 0) {
                return prevTimeLeft - 1;
            } else {
                clearInterval(intervalRef.current as NodeJS.Timeout);
                setIsRunning(false);
                setIsFinished(true);
                return prevTimeLeft;
            }
        });
    }, []);

    const start = useCallback(() => {
        if (!isRunning) {
            intervalRef.current = setInterval(intervalCallback, 1000);
            setIsRunning(true);
        }
    }, [intervalCallback, isRunning]);

    const reset = useCallback(() => {
        clearInterval(intervalRef.current as NodeJS.Timeout);
        setTimeLeft(duration);
        setIsFinished(false);
        setIsRunning(false);
    }, [duration]);

    const setNewDuration = useCallback(
        (newDuration: number) => {
            setDuration(newDuration);
            setTimeLeft(newDuration);
            reset();
        },
        [reset]
    );

    const formatTime = useCallback((seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }, []);

    const formattedTime = useMemo(() => formatTime(timeLeft), [timeLeft, formatTime]);

    return {
        timeLeft,
        start,
        reset,
        isFinished,
        setNewDuration,
        formattedTime
    };
};
